import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: `${API_BASE_URL}/import-export`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const importExportService = {
  // Import des données RNCP
  async importRncpData(formData, onProgress) {
    try {
      const response = await api.post('/import-rncp', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Export des données RNCP
  async exportRncpData(format, filters = {}) {
    try {
      const { sectors, levels, search } = filters;
      const params = new URLSearchParams();

      if (sectors) params.append('sectors', sectors);
      if (levels) params.append('levels', levels);
      if (search) params.append('search', search);

      const response = await api.get(`/export-rncp/${format}?${params.toString()}`, {
        responseType: 'blob', // Important pour les fichiers
      });

      // Créer un lien de téléchargement
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Déterminer l'extension du fichier
      const extension = format === 'xlsx' ? 'xlsx' : format === 'json' ? 'json' : 'csv';
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `rncp_export_${timestamp}.${extension}`;

      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Export téléchargé avec succès' };
    } catch (error) {
      throw error;
    }
  },

  // Obtenir les statistiques d'import
  async getImportStats() {
    try {
      const response = await api.get('/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtenir l'historique des imports
  async getImportHistory(page = 1, limit = 10) {
    try {
      const response = await api.get(`/history?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Valider un fichier avant import
  async validateFile(formData) {
    try {
      const response = await api.post('/validate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Télécharger automatiquement les données RNCP depuis l'API data.gouv.fr
  async downloadRncpData(autoImport = false) {
    try {
      const response = await api.post('/download-rncp', {
        autoImport
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default importExportService;